import type { AppState, ViewName } from "../types.ts";

const SKIP_KEY = "wcrm-skip-onboarding-v1";
// Marca que o usuário está NO fluxo de onboarding (começou do zero). Serve para
// mantê-lo no onboarding enquanto conecta os canais, SEM puxar de volta usuários
// existentes que já usam o CRM com 1-2 canais (esses nunca tiveram a flag).
const ONBOARDING_ACTIVE_KEY = "wcrm-onboarding-active-v1";

/** O usuário escolheu pular o onboarding e entrar no CRM sem conectar conta. */
export function hasSkippedOnboarding(): boolean {
  try {
    return localStorage.getItem(SKIP_KEY) === "1";
  } catch {
    return false;
  }
}

export function setSkippedOnboarding(skipped: boolean): void {
  try {
    if (skipped) localStorage.setItem(SKIP_KEY, "1");
    else localStorage.removeItem(SKIP_KEY);
  } catch {
    /* localStorage indisponível — ignora */
  }
}

export function isOnboardingActive(): boolean {
  try {
    return localStorage.getItem(ONBOARDING_ACTIVE_KEY) === "1";
  } catch {
    return false;
  }
}

export function setOnboardingActive(active: boolean): void {
  try {
    if (active) localStorage.setItem(ONBOARDING_ACTIVE_KEY, "1");
    else localStorage.removeItem(ONBOARDING_ACTIVE_KEY);
  } catch {
    /* localStorage indisponível — ignora */
  }
}

/** Os 3 canais que o onboarding pede para conectar. */
export const ONBOARDING_CHANNELS = ["whatsapp", "instagram", "email"] as const;
export type OnboardingChannel = (typeof ONBOARDING_CHANNELS)[number];

// Tipos de conta que NÃO são e-mail. Qualquer outro tipo conhecido (google,
// outlook, mail, imap, ...) é tratado como "email" — assim provedores de e-mail
// com nomes variados ainda contam, sem travar o usuário no onboarding.
const NON_EMAIL_TYPES = new Set(["whatsapp", "instagram", "telegram", "messenger", "linkedin", "twitter", "x"]);

/** Mapeia o accountType de uma conta para um dos 3 canais do onboarding (ou null). */
export function channelOfAccount(accountType?: string | null): OnboardingChannel | null {
  const t = (accountType || "").toLowerCase().trim();
  if (!t) return null;
  if (t === "whatsapp") return "whatsapp";
  if (t === "instagram") return "instagram";
  if (NON_EMAIL_TYPES.has(t)) return null;
  return "email";
}

/** Quantos dos 3 canais (whatsapp/instagram/email) têm ao menos uma conta. */
export function connectedChannelCount(accounts?: Array<{ accountType?: string | null }>): number {
  const set = new Set((accounts || []).map((a) => channelOfAccount(a.accountType)).filter(Boolean));
  return ONBOARDING_CHANNELS.filter((c) => set.has(c)).length;
}

export function decideView(state: AppState): ViewName {
  if (!state.authToken) return "login";
  // O usuário pode pular/continuar depois e entrar no CRM (conecta o resto pelo
  // seletor de contas dentro do CRM).
  if (hasSkippedOnboarding()) return "crm";
  const channels = connectedChannelCount(state.accounts);
  // Já conectou os 3 canais → CRM.
  if (channels >= ONBOARDING_CHANNELS.length) return "crm";
  // Usuário NOVO (nenhum canal) → onboarding.
  if (channels === 0) return "onboarding";
  // Com 1-2 canais: só mantém no onboarding quem ESTÁ no fluxo (começou do zero).
  // Usuário existente que já usava o CRM com 1-2 canais segue direto pro CRM.
  if (isOnboardingActive()) return "onboarding";
  return "crm";
}
